import { rosterApi, clientApi, staffApi } from './api';

export interface TimeClockStatus {
  shiftId: number;
  staffId: number;
  staffName: string;
  clientName: string;
  scheduledStart: string;
  scheduledEnd: string;
  actualStart?: string;
  actualEnd?: string;
  status: string;
  canClockIn: boolean;
  canClockOut: boolean;
  actualHours?: number;
  scheduledHours?: number;
}

export interface Shift {
  id?: number;
  clientId: number;
  staffId: number;
  clientName?: string;
  staffName?: string;
  clientDetails?: {
    id: number;
    name: string;
    address: string;
  };
  staffDetails?: {
    id: number;
    name: string;
    employeeId: string;
    position: string;
  };
  shiftType: 'regular' | 'respite' | 'emergency' | 'overnight' | 'sleepover';
  startDateTime: string;
  endDateTime: string;
  durationHours?: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  actualStartTime?: string;
  actualEndTime?: string;
  careInstructions?: string;
  specialRequirements?: string;
  hourlyRate: number;
  totalAmount?: number;
  notes?: string;
  supervisorNotes?: string;
  /** Work information submitted by staff at clock-out or within 7 days post-clock-out. */
  workInformation?: string;
  /** UTC timestamp of last staff-initiated edit to workInformation or travelKilometres. */
  lastStaffEditAt?: string;
  createdById?: number;
  createdAt?: string;
  updatedAt?: string;

  /** True when the shift is an inactive overnight sleepover (flat per-night billing). */
  isSleepover?: boolean;
  /** Travel kilometres recorded by staff at completion. Reimbursed/charged via FlatRates. */
  travelKilometres?: number;

  /**
   * NDIS service category — drives which NDIS Support Catalogue line item is
   * claimed. One of: PersonalCare / HomeHelp / CommunityAccess / Sleepover /
   * Transport. Defaults to PersonalCare on the server when unset.
   */
  serviceCategory?: 'PersonalCare' | 'HomeHelp' | 'CommunityAccess' | 'Sleepover' | 'Transport';
  /** NDIS claim type for this shift — appears on the invoice line. */
  claimType?:
    | 'Face-to-face support'
    | 'Provider travel'
    | 'Non face-to-face support'
    | 'NDIA requested report'
    | 'Irregular SIL support'
    | 'Telehealth'
    | 'Short notice cancellation';

  /**
   * Cancellation billing flags — only set when status === 'cancelled'.
   * Both are required at the API boundary in that case.
   */
  /** Percent of the normal shift amount to charge the client (0/50/100). */
  cancellationChargePercent?: number | null;
  /** Whether the staff is paid for this cancelled shift. */
  payStaffForCancellation?: boolean | null;

  /** GPS coords captured from staff device at clock-in / clock-out. */
  clockInLatitude?: number | null;
  clockInLongitude?: number | null;
  clockInAccuracyMetres?: number | null;
  clockOutLatitude?: number | null;
  clockOutLongitude?: number | null;
  clockOutAccuracyMetres?: number | null;

  // Recurrence fields
  isRecurring?: boolean;
  recurrenceType?: 'daily' | 'weekly' | 'monthly';
  recurrenceInterval?: number;
  daysOfWeek?: string; // comma-separated for weekly (0=Sunday, 6=Saturday)
  dayOfMonth?: number; // for monthly recurrence
  recurrenceEndDate?: string;
  maxOccurrences?: number;
  parentShiftId?: number; // References the original recurring shift
  isRecurrenceParent?: boolean; // True for the original shift that defines the pattern
}

export interface ShiftTemplate {
  clientId: number;
  staffId: number;
  shiftType: 'regular' | 'respite' | 'emergency' | 'overnight';
  startTime: string; // TimeSpan as string "HH:mm:ss"
  endTime: string; // TimeSpan as string "HH:mm:ss"
  recurrenceType: 'daily' | 'weekly' | 'monthly';
  recurrenceInterval: number;
  daysOfWeek?: string; // For weekly: comma-separated values
  dayOfMonth?: number; // For monthly: 1-31 or -1 for last day
  startDate: string;
  endDate?: string;
  maxOccurrences?: number;
  careInstructions?: string;
  specialRequirements?: string;
  hourlyRate: number;
  notes?: string;
  createdById?: number;
}

export interface RecurringShiftPreview {
  shiftDates: string[];
  totalShifts: number;
  totalHours: number;
  totalAmount: number;
  firstShiftDate?: string;
  lastShiftDate?: string;
}

export interface GenerateShiftsRequest {
  generateFromDate: string;
  generateToDate: string;
  overwriteExisting: boolean;
}

export interface DropdownOption {
  id: number;
  name: string;
  value?: string;
  label?: string;
  dateOfBirth?: string;
  employeeId?: string; // For staff
  address?: string; // For clients
}

export interface DropdownData {
  clients: DropdownOption[];
  staff: DropdownOption[];
}

export interface ShiftFilters {
  client?: number;
  staff?: number;
  status?: string;
  shiftType?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
}

export interface ShiftSwap {
  id: number;
  originalShiftId: number;
  requestedByStaffId: number;
  requestedToStaffId: number;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  requestedAt: string;
  respondedAt?: string;
  respondedBy?: number;
  comments?: string;
  originalShift?: Shift;
  requestedByStaff?: any;
  requestedToStaff?: any;
}

export interface CompleteShiftRequest {
  shiftId: number;
  staffId: number;
  completionNotes?: string;
  /** Travel km recorded by staff at completion. Reimbursed/charged via FlatRates. */
  travelKilometres?: number;
}

export interface ReviewShiftRequest {
  shiftId: number;
  reviewedBy: number;
  isAccepted: boolean;
  adminReviewNotes?: string;
}

export interface ShiftCompletion {
  shiftId: number;
  staffId: number;
  clientId: number;
  startDateTime: string;
  endDateTime: string;
  status: string;
  isCompletedByStaff: boolean;
  completedByStaffAt?: string;
  completionNotes?: string;
  workInformation?: string;
  travelKilometres?: number;
  lastStaffEditAt?: string;
  isReviewedByAdmin: boolean;
  reviewedBy?: number;
  reviewedAt?: string;
  adminReviewNotes?: string;
  isAcceptedByAdmin: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
}

export const rosterService = {
  // Shift CRUD operations
  getShifts: async (params?: ShiftFilters) => {
    // If no parameters, use the general endpoint
    if (!params || Object.keys(params).length === 0) {
      const response = await rosterApi.get<ApiResponse<any>>('/roster/Shifts');
      return response.data.data || response.data;
    }
    
    // If parameters provided, use the search endpoint which supports filtering
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Shifts/search', { params });
    return response.data.data || response.data;
  },

  getShiftsByDateRange: async (startDate: string, endDate: string): Promise<Shift[]> => {
    const response = await rosterApi.get<ApiResponse<Shift[]>>('/roster/Shifts/daterange', {
      params: { startDate, endDate }
    });
    // Handle both direct array response and wrapped API response
    if (Array.isArray(response.data)) {
      return response.data;
    }
    return response.data.data || [];
  },

  getShift: async (id: number) => {
    const response = await rosterApi.get<ApiResponse<Shift>>(`/roster/Shifts/${id}`);
    return response.data.data || response.data;
  },

  createShift: async (shiftData: Partial<Shift>) => {
    const response = await rosterApi.post<ApiResponse<Shift>>('/roster/Shifts', shiftData);
    return response.data.data || response.data;
  },

  updateShift: async (id: number, shiftData: Partial<Shift>) => {
    const response = await rosterApi.put<ApiResponse<Shift>>(`/roster/Shifts/${id}`, shiftData);
    return response.data.data || response.data;
  },

  deleteShift: async (id: number) => {
    await rosterApi.delete(`/roster/Shifts/${id}`);
  },

  deleteRecurringShiftsFrom: async (parentShiftId: number, fromDate: string) => {
    await rosterApi.delete(`/roster/Shifts/${parentShiftId}/recurring/from/${fromDate}`);
  },

  updateRecurringShiftsFrom: async (parentShiftId: number, fromDate: string, shiftData: Partial<Shift>) => {
    const response = await rosterApi.put<ApiResponse<boolean>>(`/roster/Shifts/${parentShiftId}/recurring/from/${fromDate}`, shiftData);
    return response.data.data || response.data;
  },

  updateShiftStatus: async (
    id: number,
    status: string,
    extras?: {
      cancellationChargePercent?: number | null;
      payStaffForCancellation?: boolean | null;
    }
  ) => {
    const payload: Record<string, unknown> = { status };
    if (extras?.cancellationChargePercent !== undefined) {
      payload.cancellationChargePercent = extras.cancellationChargePercent;
    }
    if (extras?.payStaffForCancellation !== undefined) {
      payload.payStaffForCancellation = extras.payStaffForCancellation;
    }
    const response = await rosterApi.patch<ApiResponse<Shift>>(`/roster/Shifts/${id}/status`, payload);
    return response.data.data || response.data;
  },

  // Dropdown data - Direct API calls to get real data from Client/Staff microservices
  getClientsDropdown: async (): Promise<DropdownOption[]> => {
    try {
      const response = await clientApi.get<ApiResponse<any>>('/clients');
      let backendData: any[] = [];
      
      // Handle different response structures
      if (response.data.data?.results) {
        backendData = response.data.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        backendData = response.data.data;
      } else if (Array.isArray(response.data)) {
        backendData = response.data;
      }
      
      // Transform client data to dropdown format with date of birth
      return backendData.map((client: any) => {
        const clientName = client.fullName || `${client.firstName} ${client.lastName}`;
        const dateOfBirth = client.dateOfBirth;
        
        // Format DOB for display (if available)
        let dobDisplay = '';
        if (dateOfBirth) {
          try {
            const dob = new Date(dateOfBirth);
            // Format as DD/MM/YYYY for better readability
            const day = dob.getDate().toString().padStart(2, '0');
            const month = (dob.getMonth() + 1).toString().padStart(2, '0');
            const year = dob.getFullYear();
            dobDisplay = ` • DOB: ${day}/${month}/${year}`;
          } catch (error) {
            dobDisplay = ` • DOB: ${dateOfBirth}`;
          }
        }
        
        // Add address if available
        let addressDisplay = '';
        if (client.address) {
          // Truncate long addresses
          const shortAddress = client.address.length > 30 ? 
            client.address.substring(0, 30) + '...' : client.address;
          addressDisplay = ` • ${shortAddress}`;
        }
        
        const finalLabel = `${clientName}${dobDisplay}${addressDisplay}`;
        
        return {
          id: client.id,
          name: clientName,
          value: client.id.toString(),
          label: finalLabel,
          dateOfBirth: dateOfBirth,
          address: client.address
        };
      });
    } catch (error) {
      console.error('Error fetching clients dropdown:', error);
      return [];
    }
  },

  getStaffDropdown: async (): Promise<DropdownOption[]> => {
    try {
      const response = await staffApi.get<ApiResponse<any>>('/staff');
      let backendData: any[] = [];
      
      // Handle different response structures
      if (response.data.data?.results) {
        backendData = response.data.data.results;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        backendData = response.data.data;
      } else if (Array.isArray(response.data)) {
        backendData = response.data;
      }
      
      // Transform staff data to dropdown format with date of birth
      return backendData.map((staff: any) => {
        const staffName = `${staff.firstName} ${staff.lastName}`;
        const dateOfBirth = staff.dateOfBirth;
        const employeeId = staff.employeeId;
        const position = staff.position;
        
        // Format DOB for display (if available)
        let dobDisplay = '';
        if (dateOfBirth) {
          try {
            const dob = new Date(dateOfBirth);
            // Format as DD/MM/YYYY for better readability
            const day = dob.getDate().toString().padStart(2, '0');
            const month = (dob.getMonth() + 1).toString().padStart(2, '0');
            const year = dob.getFullYear();
            dobDisplay = ` • DOB: ${day}/${month}/${year}`;
          } catch (error) {
            dobDisplay = ` • DOB: ${dateOfBirth}`;
          }
        }
        
        // Create label with employee ID, position, and DOB
        let label = staffName;
        if (employeeId) {
          label += ` • ID: ${employeeId}`;
        }
        if (position) {
          label += ` • ${position}`;
        }
        if (dobDisplay) {
          label += dobDisplay;
        }
        
        return {
          id: staff.id,
          name: staffName,
          value: staff.id.toString(),
          label: label,
          dateOfBirth: dateOfBirth,
          employeeId: employeeId
        };
      });
    } catch (error) {
      console.error('Error fetching staff dropdown:', error);
      return [];
    }
  },

  // Schedule operations
  getWeeklySchedule: async (startDate: string, endDate: string) => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Schedule/weekly', {
      params: { startDate, endDate }
    });
    return response.data.data || response.data;
  },

  getScheduleConflicts: async (startDate: string, endDate: string) => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Schedule/conflicts', {
      params: { startDate, endDate }
    });
    return response.data.data || response.data;
  },

  // Time Clock operations
  getStaffTodayShifts: async (staffId: number): Promise<{ data: TimeClockStatus[] }> => {
    const response = await rosterApi.get<ApiResponse<TimeClockStatus[]>>(`/roster/Shifts/staff/${staffId}/shifts/today`);
    const data = response.data.data || response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  clockIn: async (
    shiftId: number,
    staffId: number,
    notes?: string,
    location?: { latitude: number; longitude: number; accuracyMetres?: number },
  ) => {
    const response = await rosterApi.post<ApiResponse<any>>(`/roster/Shifts/${shiftId}/clock-in`, {
      shiftId,
      staffId,
      notes: notes || '',
      latitude: location?.latitude ?? null,
      longitude: location?.longitude ?? null,
      accuracyMetres: location?.accuracyMetres ?? null,
    });
    return response.data.data || response.data;
  },

  clockOut: async (
    shiftId: number,
    staffId: number,
    notes?: string,
    travelKilometres?: number,
    workInformation?: string,
    location?: { latitude: number; longitude: number; accuracyMetres?: number },
  ) => {
    const response = await rosterApi.post<ApiResponse<any>>(`/roster/Shifts/${shiftId}/clock-out`, {
      shiftId,
      staffId,
      notes: notes || '',
      travelKilometres: typeof travelKilometres === 'number' ? travelKilometres : null,
      workInformation: workInformation?.trim() || null,
      latitude: location?.latitude ?? null,
      longitude: location?.longitude ?? null,
      accuracyMetres: location?.accuracyMetres ?? null,
    });
    return response.data.data || response.data;
  },

  // Shift Swap operations
  getShiftSwaps: async (params?: any) => {
    const response = await rosterApi.get<ApiResponse<ShiftSwap[]>>('/roster/ShiftSwaps', { params });
    return response.data.data || response.data || [];
  },

  createShiftSwap: async (swapData: {
    originalShiftId: number;
    requestedToStaffId: number;
    reason: string;
  }) => {
    const response = await rosterApi.post<ApiResponse<ShiftSwap>>('/roster/ShiftSwaps', swapData);
    return response.data.data || response.data;
  },

  respondToShiftSwap: async (swapId: number, action: 'approve' | 'reject', comments?: string) => {
    const response = await rosterApi.post<ApiResponse<ShiftSwap>>(`/roster/ShiftSwaps/${swapId}/${action}`, {
      comments: comments || ''
    });
    return response.data.data || response.data;
  },

  // Legacy methods for backward compatibility
  approveShiftSwap: async (swapId: number, comments?: string) => {
    return rosterService.respondToShiftSwap(swapId, 'approve', comments);
  },

  rejectShiftSwap: async (swapId: number, comments?: string) => {
    return rosterService.respondToShiftSwap(swapId, 'reject', comments);
  },

  // Reporting
  getShiftReport: async (params: any) => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Reports/shifts', { params });
    return response.data.data || response.data;
  },

  getStaffHoursReport: async (params: any) => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Reports/staff-hours', { params });
    return response.data.data || response.data;
  },

  getClientServiceReport: async (params: any) => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Reports/client-service', { params });
    return response.data.data || response.data;
  },

  // Roster Templates (for recurring shifts)
  getShiftTemplates: async () => {
    const response = await rosterApi.get<ApiResponse<any>>('/roster/Templates');
    return response.data.data || response.data || [];
  },

  createShiftTemplate: async (templateData: any) => {
    const response = await rosterApi.post<ApiResponse<any>>('/roster/Templates', templateData);
    return response.data.data || response.data;
  },

  updateShiftTemplate: async (id: number, templateData: any) => {
    const response = await rosterApi.put<ApiResponse<any>>(`/roster/Templates/${id}`, templateData);
    return response.data.data || response.data;
  },

  deleteShiftTemplate: async (id: number) => {
    await rosterApi.delete(`/roster/Templates/${id}`);
  },

  generateShiftsFromTemplate: async (templateId: number, params: {
    startDate: string;
    endDate: string;
    skipExisting?: boolean;
  }) => {
    const response = await rosterApi.post<ApiResponse<any>>(`/roster/Templates/${templateId}/generate`, params);
    return response.data.data || response.data;
  },

  // Staff shift queries
  getStaffShifts: async (staffId: number, limit?: number, offset?: number): Promise<{ data: Shift[] }> => {
    const params = new URLSearchParams();
    if (limit !== undefined) params.append('limit', limit.toString());
    if (offset !== undefined) params.append('offset', offset.toString());
    
    const url = `/roster/Shifts/staff/${staffId}/shifts${params.toString() ? '?' + params.toString() : ''}`;
    const response = await rosterApi.get<ApiResponse<Shift[]>>(url);
    const data = response.data.data || response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  getUpcomingStaffShifts: async (staffId: number, days: number = 7): Promise<{ data: Shift[] }> => {
    const response = await rosterApi.get<ApiResponse<Shift[]>>(`/roster/Shifts/staff/${staffId}/shifts/upcoming?days=${days}`);
    const data = response.data.data || response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  getStaffShiftHistory: async (staffId: number, days: number = 30): Promise<{ data: Shift[] }> => {
    const response = await rosterApi.get<ApiResponse<Shift[]>>(`/roster/Shifts/staff/${staffId}/shifts/history?days=${days}`);
    const data = response.data.data || response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  // Shift Completion Methods
  completeShift: async (request: CompleteShiftRequest): Promise<{ data: ShiftCompletion }> => {
    const response = await rosterApi.post<ShiftCompletion>('/roster/ShiftCompletion/complete', request);
    return { data: response.data };
  },

  getPendingReviewShifts: async (): Promise<{ data: ShiftCompletion[] }> => {
    const response = await rosterApi.get<ShiftCompletion[]>('/roster/ShiftCompletion/pending-review');
    const data = response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  getShiftsCompletedByStaff: async (staffId: number): Promise<{ data: ShiftCompletion[] }> => {
    const response = await rosterApi.get<ShiftCompletion[]>(`/roster/ShiftCompletion/completed-by-staff/${staffId}`);
    const data = response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  reviewShift: async (request: ReviewShiftRequest): Promise<{ data: ShiftCompletion }> => {
    const response = await rosterApi.post<ShiftCompletion>('/roster/ShiftCompletion/review', request);
    return { data: response.data };
  },

  getShiftsByCompletionStatus: async (isCompleted: boolean, isReviewed?: boolean): Promise<{ data: ShiftCompletion[] }> => {
    let url = `/roster/ShiftCompletion/by-status?isCompleted=${isCompleted}`;
    if (isReviewed !== undefined) {
      url += `&isReviewed=${isReviewed}`;
    }
    
    const response = await rosterApi.get<ShiftCompletion[]>(url);
    const data = response.data;
    
    // Ensure we return an array
    const shifts = Array.isArray(data) ? data : [];
    return { data: shifts };
  },

  /**
   * Staff self-update: edit WorkInformation and/or TravelKilometres on a
   * completed shift. Only the assigned staff member may call this, and only
   * within 7 days of the clock-out time.
   */
  staffUpdateShift: async (
    shiftId: number,
    staffId: number,
    workInformation?: string | null,
    travelKilometres?: number | null,
  ): Promise<Shift> => {
    const response = await rosterApi.patch<ApiResponse<Shift>>(
      `/roster/Shifts/${shiftId}/staff-update`,
      {
        staffId,
        workInformation: workInformation ?? undefined,
        travelKilometres: travelKilometres ?? undefined,
      },
    );
    return response.data.data ?? (response.data as any);
  },
};

export default rosterService;
